import { ReactNode, useCallback, useRef } from 'react'

import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import isNil from 'lodash.isnil'
import { twMerge } from 'tailwind-merge'

import useDisclosure from 'src/hooks/use-disclosure'

import { getModalRoot } from '../Portal/Portal'
type Props = {
  align?: TooltipPrimitive.TooltipContentProps['align']
  asChild?: boolean
  children: React.ReactNode
  content: ReactNode
  contentClassName?: string
  delayDurationMs?: number
  side?: TooltipPrimitive.TooltipContentProps['side']
  triggerClassName?: string
} & (
  | {
      // Parent component controls open state
      isOpen: boolean
      trigger?: never
    }
  | {
      // Radix controls open state
      isOpen?: never
      trigger?: 'click' | 'focus' | 'hover'
    }
)
const Tooltip = ({
  align = 'center',
  asChild,
  children,
  content,
  contentClassName,
  delayDurationMs = 100,
  isOpen,
  side = 'top',
  trigger = 'hover',
  triggerClassName,
}: Props) => {
  // Use controlled open state only when trigger is click
  const { isOpen: isOpenInternal, close, toggle } = useDisclosure()
  const contentRef = useRef<HTMLDivElement>(null)

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLButtonElement>) => {
      if (contentRef.current?.contains(e.relatedTarget)) {
        // If the blur is triggered by clicking something inside the tooltip, don't close
        return
      }
      close()
    },
    [close]
  )

  if (!content) {
    return children
  }

  return (
    <TooltipPrimitive.Provider delayDuration={delayDurationMs} data-slot="tooltip-provider">
      <TooltipPrimitive.Root
        data-slot="tooltip"
        {...(trigger === 'click'
          ? { open: isOpenInternal }
          : !isNil(isOpen)
            ? { open: isOpen }
            : {})}
      >
        <TooltipPrimitive.Trigger
          asChild={asChild ?? true}
          className={twMerge(trigger === 'focus' ? 'pointer-events-none' : '', triggerClassName)}
          data-slot="tooltip-trigger"
          onBlur={handleBlur}
          onClick={trigger === 'click' ? toggle : undefined}
        >
          {/* Ensure 'children' is an element, so TooltipPrimitive.Trigger can forward properties to it.
          Otherwise the tooltip will fail to render. */}
          {typeof children === 'string' ? <>{children}</> : children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal container={getModalRoot()}>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={2}
            align={align}
            data-slot="tooltip-content"
            // TODO: Remove z-2000 once we stop using antd dropdowns with tooltips
            className="animate-in isolate z-2000 w-fit bg-transparent text-xs text-neutral-950"
            ref={contentRef}
          >
            <TooltipPrimitive.Arrow className="size-1.5 w-3 translate-y-[calc(-25%)] rounded-[2px] fill-white stroke-neutral-200 stroke-[2px] [stroke-dasharray:30] [stroke-dashoffset:-33]" />
            <div
              className={twMerge(
                'max-h-[var(--radix-tooltip-content-available-height)] max-w-[min(90vw,_400px)] overflow-y-auto rounded-md border border-neutral-200 bg-white px-3 py-1.5 shadow-lg',
                contentClassName
              )}
            >
              {content}
            </div>
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}

export default Tooltip
